import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Input } from '@mlm/ui';
import { previewSlotByCode } from '../lib/api';

/**
 * Pick a sponsor SLOT by its slot_code (or referral alias) for admin settings
 * that route registrations under a specific slot (walk-in default / link
 * override). Stores the slot_code STRING — resolved + re-validated active at use
 * time server-side. Debounced 300ms preview confirms the owner before saving.
 *
 * Controlled: parent owns the slot_code. Pass `value` (current code or null) +
 * `onChange` (called with the picked code or null on clear).
 */
export function SlotCodePicker({
  value,
  onChange,
  disabled,
  placeholder = 'Enter a slot code…',
}: {
  value: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim().toUpperCase()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Resolve the currently-saved code so the card shows who it points at.
  const savedQ = useQuery({
    queryKey: ['slot-preview', value],
    queryFn: () => previewSlotByCode(value!),
    enabled: !!value,
    staleTime: 60_000,
  });

  // Resolve what the admin is typing (only when it's a new, non-saved code).
  const typedQ = useQuery({
    queryKey: ['slot-preview-typed', debounced],
    queryFn: () => previewSlotByCode(debounced),
    enabled: !value && debounced.length > 0,
    staleTime: 30_000,
  });

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 p-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">
            <span className="font-mono">{value}</span>
            {savedQ.data ? ` · ${savedQ.data.ownerFullName ?? savedQ.data.ownerEmail}` : ''}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {savedQ.isLoading
              ? 'Resolving…'
              : savedQ.data
                ? `${savedQ.data.ownerEmail} · ${savedQ.data.membershipCode} (${savedQ.data.slotType})`
                : 'No active slot resolves to this code.'}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => onChange(null)}
        >
          Clear
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value.toUpperCase())}
        disabled={disabled}
        placeholder={placeholder}
      />
      {debounced.length > 0 && (
        <div className="rounded-md border bg-popover p-2 text-sm shadow-sm">
          {typedQ.isLoading ? (
            <p className="text-xs text-muted-foreground">Resolving…</p>
          ) : typedQ.data ? (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-medium">
                  <span className="font-mono">{typedQ.data.slotCode}</span> ·{' '}
                  {typedQ.data.ownerFullName ?? typedQ.data.ownerEmail}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {typedQ.data.ownerEmail} · {typedQ.data.membershipCode} ({typedQ.data.slotType})
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                disabled={disabled}
                onClick={() => {
                  onChange(typedQ.data!.slotCode);
                  setQuery('');
                }}
              >
                Use
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No active slot found for that code.</p>
          )}
        </div>
      )}
    </div>
  );
}
